import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  FileText,
  Loader2,
  Receipt,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useFeatures } from "@/context/FeaturesContext";
import { useAuth } from "@/context/AuthContext";
import { downloadBillingHistoryPdf, downloadBillingHistoryZip } from "@/lib/apiClient";
import { canAccessAdminSettings } from "@/lib/settingsAccess";

const PROOF_BUCKET = "tenant-payment-proofs";
const PLAN_OPTIONS = [
  {
    code: "solo",
    label: "Solo",
    monthly_price_centavos: 99900,
    active_doctor_limit_per_branch: 1,
    active_branch_limit: 1,
    active_staff_limit: 2,
    description: "For one-chair or owner-led clinics starting lean.",
    badge: null,
    features: [
      { label: "Core clinic workflow", included: true },
      { label: "Billing + payment review", included: true },
      { label: "Clinic Health", included: true },
      { label: "Tx Plan Center", included: false },
      { label: "Tx Plan Dynamic Charting", included: false },
      { label: "Ortho + Medical History", included: false },
    ],
  },
  {
    code: "clinic",
    label: "Clinic",
    monthly_price_centavos: 299900,
    original_price_centavos: 299900,
    active_doctor_limit_per_branch: 5,
    active_branch_limit: 2,
    active_staff_limit: 10,
    description: "For growing clinics that need the full operating system.",
    badge: "Most common",
    features: [
      { label: "Core clinic workflow", included: true },
      { label: "Billing + payment review", included: true },
      { label: "Reports", included: true },
      { label: "Tx Plan Center", included: true },
      { label: "Tx Plan Dynamic Charting", included: true },
      { label: "Ortho + Medical History", included: true },
    ],
  },
  {
    code: "enterprise",
    label: "Enterprise",
    monthly_price_centavos: 999900,
    description: "For multi-branch groups that need heavier oversight.",
    badge: null,
  },
];

const STATUS_STYLES = {
  trialing: "border-amber-200 bg-amber-50 text-amber-800",
  pending_payment: "border-amber-200 bg-amber-50 text-amber-900",
  active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  active_pending_verification: "border-blue-200 bg-blue-50 text-blue-800",
  past_due: "border-rose-200 bg-rose-50 text-rose-800",
  suspended: "border-rose-300 bg-rose-100 text-rose-900",
  cancelled: "border-slate-300 bg-slate-100 text-slate-700",
};

const INPUT_CLASS =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100";

const BILLING_HISTORY_STATUS_OPTIONS = ["All", "Paid", "Failed", "Credit", "Refunded", "Pending"];

function paidDateToManilaTimestamp(dateKey) {
  return dateKey ? `${dateKey}T00:00:00+08:00` : null;
}

const BILLING_HISTORY_STATUS_META = {
  Paid: {
    Icon: Receipt,
    tileClass: "bg-emerald-50 text-emerald-700",
    pillClass: "bg-emerald-50 text-emerald-800",
  },
  Failed: {
    Icon: AlertCircle,
    tileClass: "bg-rose-50 text-rose-700",
    pillClass: "bg-rose-50 text-rose-800",
  },
  Credit: {
    Icon: RefreshCw,
    tileClass: "bg-slate-100 text-slate-600",
    pillClass: "bg-slate-100 text-slate-700",
  },
  Refunded: {
    Icon: ArrowLeft,
    tileClass: "bg-slate-100 text-slate-600",
    pillClass: "bg-slate-100 text-slate-700",
  },
  Pending: {
    Icon: Clock,
    tileClass: "bg-amber-50 text-amber-700",
    pillClass: "bg-amber-50 text-amber-800",
  },
};

export default function SettingsBilling() {
  const { refreshFeatures } = useFeatures();
  const { clinicRole, isPlatformAdmin } = useAuth();
  const canManageBilling = canAccessAdminSettings(clinicRole, isPlatformAdmin);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portal, setPortal] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(() => manilaDateKey());
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrSuggestion, setOcrSuggestion] = useState(null);
  const [selectingPlan, setSelectingPlan] = useState(null);
  const [activatingPlan, setActivatingPlan] = useState(false);
  const [planReviewCode, setPlanReviewCode] = useState(null);
  const [planPrice, setPlanPrice] = useState(null);
  const [historyPayload, setHistoryPayload] = useState({ can_view: false, can_retry: false, rows: [] });
  const [historyFilter, setHistoryFilter] = useState("All");
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);
  const [historyDownloading, setHistoryDownloading] = useState(null);
  const [securityRecommendationDismissed, setSecurityRecommendationDismissed] = useState(false);
  const ocrTriedRef = useRef(null);

  const load = useCallback(async () => {
    if (!canManageBilling) {
      setLoading(false);
      setError(null);
      setPortal(null);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase.rpc("tenant_get_billing_portal");
    setLoading(false);
    if (e) {
      setError(e.message || "Could not load billing.");
      return;
    }
    const nextPortal = data || {};
    setPortal(nextPortal);
    const { data: historyData, error: historyError } = await supabase.rpc("tenant_get_billing_history");
    setHistoryPayload(historyError ? { can_view: false, can_retry: false, rows: [] } : normalizeBillingHistoryPayload(historyData));
    const activePlanCode = nextPortal?.subscription?.plan_code;
    const nextBillingDate = nextPortal?.subscription?.status === "trialing"
      ? (nextPortal?.subscription?.trial_ends_at || nextPortal?.subscription?.current_period_end)
      : (nextPortal?.subscription?.next_invoice_date || nextPortal?.subscription?.current_period_end);
    if (nextBillingDate && ["solo", "clinic"].includes(activePlanCode)) {
      const { data: priceData, error: priceError } = await supabase.rpc("get_subscription_plan_price", {
        p_plan_code: activePlanCode,
      });
      setPlanPrice(priceError ? null : (priceData || null));
    } else {
      setPlanPrice(null);
    }
  }, [canManageBilling]);

  useEffect(() => { load(); }, [load]);

  const invoice = portal?.open_invoice || null;
  const tenant = portal?.tenant || {};
  const subscription = portal?.subscription || {};
  const access = portal?.access || {};
  const invoiceRequiresPayment = isPayableInvoice(invoice);
  const accountStatus = access.status || subscription.status;
  const readOnlyCopy = readOnlyBillingCopy(access.reason);
  const latestSubmission = portal?.latest_submission || null;
  const invoiceSubmission = portal?.invoice_submission
    || (latestSubmission && (!invoice?.id || latestSubmission.invoice_id === invoice.id) ? latestSubmission : null);
  const proofAlreadySubmitted = ["pending_review", "approved"].includes(invoiceSubmission?.status);
  const activePeriod = billingPeriodText(subscription);
  const billingTimeLeftItems = buildBillingTimeLeftItems(subscription);
  const subscriptionPlanLabel = subscriptionPlanName(subscription);
  const liveBranchAddOns = buildLiveBranchAddOnBreakdown(portal?.branch_add_ons, subscription);
  const nextInvoiceDisplayDate = subscription.status === "trialing"
    ? (subscription.trial_ends_at || subscription.current_period_end)
    : subscription.current_period_end;
  const upcomingPriceCopy = buildUpcomingPriceCopy(subscription, planPrice, liveBranchAddOns);
  const methods = Array.isArray(portal?.payment_methods) ? portal.payment_methods : [];
  const planOptions = normalizePlanOptions(portal?.plan_options);
  const selfServePlanOptions = planOptions.filter((plan) => ["solo", "clinic"].includes(plan.code));
  const needsPlanSelection = subscription.status === "trialing"
    && subscription.assignment_source === "self_serve_trial"
    && subscription.plan_code === "trial";
  const canManagePlan = !needsPlanSelection
    && !!subscription.id
    && ["trialing", "active", "active_pending_verification", "past_due"].includes(subscription.status)
    && selfServePlanOptions.length > 0;
  const canStartPaidBillingNow = subscription.status === "trialing"
    && subscription.assignment_source === "self_serve_plan_selection"
    && subscription.plan_code
    && subscription.plan_code !== "trial"
    && Number(subscription.monthly_price_centavos_locked || 0) > 0
    && isWithinTrialPayNowWindow(subscription.trial_ends_at)
    && !invoice;

  useEffect(() => {
    if (invoice?.amount_centavos && !amount) {
      setAmount((Number(invoice.amount_centavos) / 100).toFixed(2));
    }
  }, [invoice?.amount_centavos, amount]);

  useEffect(() => {
    if (proofAlreadySubmitted && payOpen) {
      setPayOpen(false);
    }
  }, [proofAlreadySubmitted, payOpen]);

  useEffect(() => {
    if (!proofAlreadySubmitted && proofOpen) {
      setProofOpen(false);
    }
  }, [proofAlreadySubmitted, proofOpen]);

  // Auto-OCR the receipt as soon as it's picked. Best-effort: failures are
  // silent and staff can always type the reference manually. Tesseract.js
  // is lazy-loaded so the ~5MB worker only ships when actually needed.
  useEffect(() => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) return;
    if (ocrTriedRef.current === file) return;
    ocrTriedRef.current = file;
    let cancelled = false;
    setOcrRunning(true);
    setOcrSuggestion(null);
    (async () => {
      try {
        const Tesseract = await import("tesseract.js");
        const { data } = await Tesseract.recognize(file, "eng");
        if (cancelled) return;
        const text = (data?.text || "").replace(/\s+/g, " ");
        const patterns = [
          /\b(\d{4}[\s-]\d{3}[\s-]\d{6})\b/,
          /\b(\d{4}[\s-]\d{4}[\s-]\d{4})\b/,
          /\bRef(?:\.|erence)?[\s:#]*([A-Z0-9-]{8,})\b/i,
          /\b(\d{12,16})\b/,
        ];
        for (const re of patterns) {
          const m = text.match(re);
          if (m) {
            const cand = m[1].replace(/\s/g, "");
            if (!cancelled) {
              setOcrSuggestion(cand);
              setReference((current) => current || cand);
            }
            break;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[billing-ocr] failed", e);
      }
      if (!cancelled) setOcrRunning(false);
    })();
    return () => { cancelled = true; };
  }, [file]);

  const amountCentavos = useMemo(() => pesosToCentavos(amount), [amount]);
  const historyRows = useMemo(
    () => normalizeBillingHistoryRows(historyPayload?.rows),
    [historyPayload?.rows],
  );

  if (!canManageBilling) {
    return (
      <div data-testid="settings-billing-denied" className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <Receipt className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">Only a Clinic Admin can manage billing.</p>
      </div>
    );
  }

  function openPay() {
    if (proofAlreadySubmitted) {
      setError("Payment proof is already uploaded and waiting for review.");
      return;
    }
    setError(null);
    setPaidAt((current) => current || manilaDateKey());
    setPayOpen(true);
  }

  function closePay() {
    if (submitting) return;
    setPayOpen(false);
    setError(null);
  }

  async function submitProof(event) {
    event.preventDefault();
    if (!invoice?.id) {
      setError("No open invoice to pay.");
      return;
    }
    if (proofAlreadySubmitted) {
      setError("Payment proof is already uploaded and waiting for review.");
      return;
    }
    if (!file) {
      setError("Upload a payment proof file.");
      return;
    }
    if (!reference.trim()) {
      setError("Enter the payment reference.");
      return;
    }
    if (!paidAt) {
      setError("Enter the date paid.");
      return;
    }
    if (amountCentavos <= 0) {
      setError("Enter the amount paid.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
      const path = `${tenant.id}/${invoice.id}-${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from(PROOF_BUCKET)
        .upload(path, file, { upsert: false });
      if (uploadError) throw uploadError;

      const { error: rpcError } = await supabase.rpc("tenant_submit_payment_proof_with_paid_at", {
        p_invoice_id: invoice.id,
        p_amount_centavos: amountCentavos,
        p_reference: reference.trim(),
        p_paid_via: null,
        p_receipt_storage_path: path,
        p_paid_at: paidDateToManilaTimestamp(paidAt),
      });
      if (rpcError) throw rpcError;

      setReference("");
      setAmount("");
      setPaidAt(manilaDateKey());
      setFile(null);
      setOcrSuggestion(null);
      ocrTriedRef.current = null;
      setPayOpen(false);
      await load();
    } catch (err) {
      setError(err.message || "Could not submit proof.");
    } finally {
      setSubmitting(false);
    }
  }

  const reviewedPlan = selfServePlanOptions.find((plan) => plan.code === planReviewCode) || null;
  const planReview = buildPlanChangeReview({
    subscription,
    currentPlan: selfServePlanOptions.find((plan) => plan.code === subscription.plan_code) || null,
    nextPlan: reviewedPlan,
    accountCreditBalanceCentavos: portal?.account_credit_balance_centavos,
    hasOpenInvoice: Boolean(invoice),
  });

  function requestPlanReview(planCode) {
    setError(null);
    setPlanReviewCode(planCode);
  }

  async function selectPlan(planCode = planReviewCode) {
    if (!planCode) return;
    setSelectingPlan(planCode);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("tenant_initiate_plan_selection", {
      p_plan_code: planCode,
    });
    if (rpcError) {
      setSelectingPlan(null);
      setError(rpcError.message || "Could not choose plan.");
      return;
    }
    setPlanReviewCode(null);
    if (isBillingPortalPayload(data)) {
      setPortal(data);
    } else {
      await load();
    }
    setSelectingPlan(null);
    await refreshFeatures?.();
  }

  async function startPaidBillingNow() {
    if (!subscription.plan_code || activatingPlan) return;
    setActivatingPlan(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("tenant_start_subscription_now", {
      p_plan_code: subscription.plan_code,
    });
    setActivatingPlan(false);
    if (rpcError) {
      setError(rpcError.message || "Could not start paid billing.");
      return;
    }
    const nextPortal = data || {};
    setPortal(nextPortal);
    setPayOpen(Boolean(nextPortal.open_invoice));
    await refreshFeatures?.();
  }

  async function downloadHistoryPdf(row) {
    if (!row?.history_id || !row?.pdf_url) return;
    setHistoryDownloading(`pdf:${row.history_id}`);
    setError(null);
    try {
      const result = await downloadBillingHistoryPdf(row.history_id, `${safeDownloadFileSegment(row.invoice_number || "invoice")}.pdf`);
      if (!result?.ok) setError(result?.message || "Could not download invoice PDF.");
    } finally {
      setHistoryDownloading(null);
    }
  }

  async function downloadAllHistory() {
    setHistoryDownloading("zip");
    setError(null);
    try {
      const result = await downloadBillingHistoryZip(billingHistoryZipFilename(tenant?.name));
      if (!result?.ok) setError(result?.message || "Could not download billing history.");
    } finally {
      setHistoryDownloading(null);
    }
  }

  function retryHistoryPayment(row) {
    if (!row || row.status !== "Failed") return;
    if (!historyPayload?.can_retry) return;
    if (invoiceRequiresPayment && invoice?.id && row.invoice_id === invoice.id && !proofAlreadySubmitted) {
      setSelectedHistoryRow(null);
      openPay();
      return;
    }
    setError("Retry is available for the current open invoice only.");
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-white p-8 text-center text-sm text-slate-400 shadow-sm">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading...
      </div>
    );
  }

  if (error && !portal) {
    return <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>;
  }

  return (
    <div className="space-y-4" data-testid="settings-billing">
      {/* === Header card === */}
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-950">Billing</h1>
            <p className="mt-0.5 text-xs text-slate-500">{tenant.name || "Your clinic group"} subscription and payment status.</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[accountStatus] || STATUS_STYLES.active}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
            {formatStatus(accountStatus)}
          </span>
        </div>

        {["active", "active_pending_verification"].includes(accountStatus) && activePeriod ? (
          <p className="mt-2 text-xs font-semibold text-slate-600" data-testid="active-period">
            Active {activePeriod}
          </p>
        ) : null}

        {access.mode === "read_only" ? (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">{readOnlyCopy.title}</p>
              <p className="mt-1 text-xs">{readOnlyCopy.body}</p>
            </div>
          </div>
        ) : null}

        {subscription.status === "active_pending_verification" ? (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-900">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">Payment proof submitted.</p>
              <p className="mt-1 text-xs">Access stays open while Dental OS reviews the proof.</p>
            </div>
          </div>
        ) : null}
      </div>

      {!securityRecommendationDismissed ? (
        <AccountSecurityRecommendation
          onDismiss={() => setSecurityRecommendationDismissed(true)}
        />
      ) : null}

      {/* === Stats row === */}
      <div className={`grid gap-3 ${liveBranchAddOns.hasAddOns ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
        <Stat label="Plan" value={subscriptionPlanLabel} />
        <Stat
          label={liveBranchAddOns.hasAddOns ? "Base fee" : "Monthly fee"}
          value={money(liveBranchAddOns.basePriceCentavos)}
        />
        {liveBranchAddOns.hasAddOns ? (
          <Stat
            label="Branch add-ons"
            value={`${liveBranchAddOns.billableBranchCount} paid branch${liveBranchAddOns.billableBranchCount === 1 ? "" : "es"} · ${money(liveBranchAddOns.addOnTotalCentavos)}/mo`}
          />
        ) : null}
        <BillingTimeLeftCard items={billingTimeLeftItems} fallback={activePeriod} />
      </div>

      {upcomingPriceCopy ? (
        <div className="rounded-2xl border border-teal-200 bg-teal-50 px-5 py-4 text-sm text-teal-950" data-testid="upcoming-plan-price">
          <div className="flex items-start gap-2">
            <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{upcomingPriceCopy}</p>
          </div>
        </div>
      ) : null}

      {subscription.status !== "trialing" && Number(portal?.account_credit_balance_centavos || 0) > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-950 shadow-sm">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Account credit</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums">{money(portal.account_credit_balance_centavos)}</p>
          </div>
          <p className="text-xs text-emerald-800">Automatically applies to the next Dental OS invoice.</p>
        </div>
      ) : null}

      {needsPlanSelection ? (
        <PlanSelectionPanel
          mode="trial"
          plans={selfServePlanOptions}
          trialEndsAt={subscription.trial_ends_at || subscription.current_period_end}
          selectingPlan={selectingPlan}
          onSelect={requestPlanReview}
        />
      ) : null}

      {canManagePlan ? (
        <PlanSelectionPanel
          mode="manage"
          plans={selfServePlanOptions}
          currentPlanCode={subscription.plan_code}
          currentPlanLabel={subscriptionPlanLabel}
          selectingPlan={selectingPlan}
          onSelect={requestPlanReview}
        />
      ) : null}

      {canStartPaidBillingNow ? (
        <TrialPayNowPanel
          subscription={subscription}
          activating={activatingPlan}
          onStart={startPaidBillingNow}
        />
      ) : null}

      {reviewedPlan ? (
        <PlanChangeReviewModal
          review={planReview}
          saving={selectingPlan === reviewedPlan.code}
          onCancel={() => { if (!selectingPlan) setPlanReviewCode(null); }}
          onConfirm={() => selectPlan(reviewedPlan.code)}
        />
      ) : null}

      {/* === Open invoice block === */}
      {invoice ? (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="open-invoice-card">
          <div className="px-5 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                  <Receipt className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">{invoiceTitle(invoice)}</p>
                  <p className="mt-0.5 font-mono text-xs text-slate-500">{invoice.invoice_number}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold tabular-nums text-slate-950">{money(invoice.amount_centavos)}</p>
                <p className="mt-0.5 flex items-center justify-end gap-1 text-xs text-slate-500">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Due {dateOnly(invoice.due_date)}
                </p>
              </div>
            </div>

            {invoice.invoice_kind === "plan_change_adjustment" ? (
              <PlanAdjustmentBreakdown invoice={invoice} />
            ) : invoice.invoice_kind === "branch_addon_adjustment" ? (
              <BranchAddOnAdjustmentBreakdown invoice={invoice} />
            ) : (
              <SubscriptionInvoiceBreakdown invoice={invoice} />
            )}

            {invoiceSubmission ? <PaymentProofStatus submission={invoiceSubmission} /> : null}

            <div className="mt-5 flex justify-end">
              {!invoiceRequiresPayment ? (
                <div className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-2.5 text-sm font-semibold text-emerald-800 sm:w-auto">
                  <CheckCircle2 className="h-4 w-4" />
                  No payment needed
                </div>
              ) : proofAlreadySubmitted ? (
                <button
                  type="button"
                  onClick={() => setProofOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-6 py-2.5 text-sm font-semibold text-blue-800 transition-colors hover:bg-blue-100 sm:w-auto"
                  data-testid="view-payment"
                >
                  <FileText className="h-4 w-4" />
                  View payment
                </button>
              ) : (
                <button
                  type="button"
                  onClick={openPay}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 sm:w-auto"
                  data-testid="open-pay-modal"
                >
                  <Receipt className="h-4 w-4" />
                  {isAdjustmentInvoice(invoice) ? "Pay adjustment" : "Pay subscription"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <Receipt className="h-5 w-5" />
          </div>
          <p className="text-sm font-semibold text-slate-900">No open invoice right now.</p>
          <p className="text-xs text-slate-500">
            {nextInvoiceDisplayDate
              ? `Your next invoice will appear here on ${dateOnly(nextInvoiceDisplayDate)}.`
              : "Your next invoice will appear here when it’s ready."}
          </p>
        </div>
      )}

      {historyPayload?.can_view ? (
        <BillingHistorySection
          rows={historyRows}
          canRetry={Boolean(historyPayload?.can_retry)}
          currentPeriodEnd={nextInvoiceDisplayDate}
          filter={historyFilter}
          expanded={historyExpanded}
          downloading={historyDownloading}
          onFilterChange={(value) => {
            setHistoryFilter(value);
            setHistoryExpanded(false);
          }}
          onToggleExpanded={() => setHistoryExpanded((current) => !current)}
          onOpenRow={setSelectedHistoryRow}
          onDownloadAll={downloadAllHistory}
        />
      ) : null}

      {payOpen && invoice && invoiceRequiresPayment && !proofAlreadySubmitted ? (
        <SubscriptionPayModal
          invoice={invoice}
          methods={methods}
          amount={amount}
          setAmount={setAmount}
          paidAt={paidAt}
          setPaidAt={setPaidAt}
          subscription={subscription}
          reference={reference}
          setReference={setReference}
          file={file}
          setFile={setFile}
          ocrRunning={ocrRunning}
          ocrSuggestion={ocrSuggestion}
          submitting={submitting}
          error={error}
          onClose={closePay}
          onSubmit={submitProof}
        />
      ) : null}

      {proofOpen && invoiceSubmission ? (
        <PaymentProofModal
          submission={invoiceSubmission}
          onClose={() => setProofOpen(false)}
        />
      ) : null}

      {selectedHistoryRow ? (
        <BillingHistoryDetail
          row={selectedHistoryRow}
          canRetry={Boolean(historyPayload?.can_retry)}
          downloading={historyDownloading === `pdf:${selectedHistoryRow.history_id}`}
          onClose={() => setSelectedHistoryRow(null)}
          onDownload={() => downloadHistoryPdf(selectedHistoryRow)}
          onRetry={() => retryHistoryPayment(selectedHistoryRow)}
        />
      ) : null}
    </div>
  );
}

function AccountSecurityRecommendation({ onDismiss }) {
  return (
    <section
      className="rounded-2xl border border-teal-100 bg-teal-50/70 px-4 py-3 text-sm text-teal-950 shadow-sm"
      data-testid="billing-security-recommendation"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-teal-700 shadow-sm">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold">Strengthen account security</p>
            <p className="mt-0.5 text-xs leading-5 text-teal-800">
              Add two-factor authentication from your profile when you are ready. Billing stays available to admins.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:pl-3">
          <a
            href="/settings/profile"
            className="inline-flex items-center justify-center rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-800"
          >
            Set up two-factor
          </a>
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-teal-700 transition hover:bg-teal-100"
            aria-label="Dismiss security recommendation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function BillingHistorySection({
  rows,
  canRetry,
  currentPeriodEnd,
  filter,
  expanded,
  downloading,
  onFilterChange,
  onToggleExpanded,
  onOpenRow,
  onDownloadAll,
}) {
  const filteredRows = filter === "All" ? rows : rows.filter((row) => row.status === filter);
  const visibleRows = expanded ? filteredRows : filteredRows.slice(0, 10);
  const showFooter = rows.length > 10 || filter !== "All";
  const countText = filter === "All"
    ? `Showing ${visibleRows.length} of ${rows.length} invoices.`
    : `Showing ${visibleRows.length} of ${rows.length} invoices, filtered by ${filter}.`;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="billing-history-section">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">Billing history</h2>
          <p className="mt-1 text-xs text-slate-500">All past invoices and payments.</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <select
            value={filter}
            onChange={(event) => onFilterChange(event.target.value)}
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
            data-testid="billing-history-status-filter"
            aria-label="Billing history status filter"
          >
            {BILLING_HISTORY_STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onDownloadAll}
            disabled={downloading === "zip" || rows.length === 0}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="billing-history-download-all"
          >
            {downloading === "zip" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download all
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-5 flex min-h-48 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 px-4 py-10 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <Receipt className="h-5 w-5" />
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-900">No billing history yet.</p>
          <p className="mt-1 text-xs text-slate-500">Your next invoice will appear here on {dateOnly(currentPeriodEnd)}.</p>
        </div>
      ) : (
        <>
          <div className="mt-5 hidden overflow-hidden rounded-xl border border-slate-200 md:block">
            <div className="grid grid-cols-[1.25fr_1.7fr_0.85fr_0.9fr_0.8fr_0.55fr] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <div>Invoice</div>
              <div>Description</div>
              <div>Date</div>
              <div className="text-right">Amount</div>
              <div className="text-center">Status</div>
              <div className="text-right">Action</div>
            </div>
            {filteredRows.length === 0 ? (
              <BillingHistoryFilteredEmpty />
            ) : visibleRows.map((row) => (
              <BillingHistoryTableRow key={row.history_id} row={row} onOpenRow={onOpenRow} />
            ))}
          </div>

          <div className="mt-5 grid gap-3 md:hidden">
            {filteredRows.length === 0 ? (
              <BillingHistoryFilteredEmpty />
            ) : visibleRows.map((row) => (
              <BillingHistoryMobileCard key={row.history_id} row={row} onOpenRow={onOpenRow} />
            ))}
          </div>

          {showFooter ? (
            <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">{countText}</p>
              {rows.length > 10 ? (
                <button
                  type="button"
                  onClick={onToggleExpanded}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                  data-testid="billing-history-view-all"
                >
                  {expanded ? "Show less" : "View all"}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function BillingHistoryTableRow({ row, onOpenRow }) {
  return (
    <button
      type="button"
      className="grid w-full grid-cols-[1.25fr_1.7fr_0.85fr_0.9fr_0.8fr_0.55fr] gap-3 border-b border-slate-100 px-4 py-4 text-left text-sm transition last:border-b-0 hover:bg-slate-50"
      onClick={() => onOpenRow(row)}
      data-testid={`billing-history-row-${row.history_id}`}
    >
      <BillingHistoryInvoiceCell row={row} />
      <div className="self-center text-slate-700">{row.description || "-"}</div>
      <div className="self-center tabular-nums text-slate-700">{dateOnly(row.issued_date)}</div>
      <div className="self-center text-right font-bold tabular-nums text-slate-950">{historyMoney(row.amount_centavos)}</div>
      <div className="self-center text-center"><BillingHistoryStatusPill status={row.status} /></div>
      <div className="self-center text-right">
        <span className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-sm">
          View
        </span>
      </div>
    </button>
  );
}

function BillingHistoryMobileCard({ row, onOpenRow }) {
  return (
    <button
      type="button"
      className="rounded-xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm transition hover:bg-slate-50"
      onClick={() => onOpenRow(row)}
      data-testid={`billing-history-mobile-row-${row.history_id}`}
    >
      <BillingHistoryInvoiceCell row={row} />
      <p className="mt-3 text-sm text-slate-600">{row.description || "-"}</p>
      <p className="mt-1 text-xs text-slate-500">{dateOnly(row.issued_date)}</p>
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="font-bold tabular-nums text-slate-950">{historyMoney(row.amount_centavos)}</p>
        <BillingHistoryStatusPill status={row.status} />
      </div>
    </button>
  );
}

function BillingHistoryInvoiceCell({ row }) {
  const meta = billingHistoryStatusMeta(row.status);
  const Icon = meta.Icon;
  return (
    <div className="flex min-w-0 items-center gap-3 self-center">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${meta.tileClass}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 break-words font-semibold leading-5 text-slate-950">{row.invoice_number || "-"}</span>
    </div>
  );
}

function BillingHistoryStatusPill({ status }) {
  const meta = billingHistoryStatusMeta(status);
  return (
    <span className={`inline-flex min-w-16 items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${meta.pillClass}`}>
      {status || "Pending"}
    </span>
  );
}

function BillingHistoryFilteredEmpty() {
  return (
    <div className="px-4 py-8 text-center text-sm text-slate-500">
      No invoices match this filter.
    </div>
  );
}

function BillingHistoryDetail({ row, canRetry, downloading, onClose, onDownload, onRetry }) {
  const lineItems = Array.isArray(row.line_items) && row.line_items.length
    ? row.line_items
    : [{ label: row.description || "Invoice", amount_centavos: row.amount_centavos }];
  const paymentInfo = row.payment_info || {};
  const canDownload = Boolean(row.pdf_url);
  const showRetry = row.status === "Failed" && canRetry;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60" onClick={onClose}>
      <aside
        className="ml-auto flex h-full w-full flex-col bg-white shadow-2xl sm:max-w-xl"
        onClick={(event) => event.stopPropagation()}
        data-testid="billing-history-detail"
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="break-words text-lg font-bold text-slate-950">{row.invoice_number || "Invoice"}</p>
            <div className="mt-2"><BillingHistoryStatusPill status={row.status} /></div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded p-1 text-slate-500 transition hover:bg-slate-100"
            aria-label="Close invoice detail"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm sm:grid-cols-2">
            <DetailItem label="Date issued" value={dateOnly(row.issued_date)} />
            <DetailItem label="Billing period" value={`${dateOnly(row.billing_period_start)} ~ ${dateOnly(row.billing_period_end)}`} />
            <DetailItem label="Plan" value={row.plan_name || "-"} />
            <DetailItem label="Clinic" value={row.clinic_name || "-"} />
          </div>

          <div className="mt-5">
            <h3 className="text-sm font-semibold text-slate-950">Line items</h3>
            <div className="mt-2 overflow-hidden rounded-xl border border-slate-200">
              {lineItems.map((item, index) => (
                <div key={`${item.label || "item"}-${index}`} className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0">
                  <span className="text-slate-700">{item.label || "Invoice item"}</span>
                  <span className="font-semibold tabular-nums text-slate-950">{historyMoney(item.amount_centavos)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between text-base font-bold text-slate-950">
              <span>Total</span>
              <span className="tabular-nums">{historyMoney(row.amount_centavos)}</span>
            </div>
          </div>

          <div className="mt-5">
            <h3 className="text-sm font-semibold text-slate-950">Payment information</h3>
            <div className="mt-2 grid gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm">
              <DetailItem label="Method" value={paymentMethodCopy(paymentInfo)} />
              <DetailItem label="Payment date" value={dateTime(paymentInfo.paid_at || paymentInfo.payment_date)} />
              {paymentInfo.failure_reason || row.failure_reason ? (
                <DetailItem label="Failure reason" value={paymentInfo.failure_reason || row.failure_reason} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onDownload}
            disabled={!canDownload || downloading}
            title={canDownload ? undefined : "PDF not available. Contact support."}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="billing-history-download-pdf"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download PDF
          </button>
          {showRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800"
            >
              <Receipt className="h-4 w-4" />
              Retry payment
            </button>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 break-words text-sm font-medium text-slate-800">{value || "-"}</p>
    </div>
  );
}

function TrialPayNowPanel({ subscription, activating, onStart }) {
  const planName = subscription.plan_name || planLabel(subscription.plan_code);
  return (
    <section className="rounded-2xl border border-teal-200 bg-teal-50 px-5 py-4 shadow-sm" data-testid="trial-pay-now-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-700">Start paid billing</p>
          <h2 className="mt-1 text-lg font-semibold text-teal-950">Ready to activate {planName}?</h2>
          <p className="mt-1 text-xs leading-5 text-teal-800">
            This creates the first invoice now. After proof upload, access stays open while Dental OS reviews the payment.
          </p>
        </div>
        <button
          type="button"
          onClick={onStart}
          disabled={activating}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          data-testid="trial-pay-now"
        >
          {activating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
          {activating ? "Creating invoice..." : "Pay now"}
        </button>
      </div>
    </section>
  );
}

function PlanSelectionPanel({
  plans,
  mode = "trial",
  currentPlanCode,
  currentPlanLabel,
  trialEndsAt,
  selectingPlan,
  onSelect,
}) {
  const isManage = mode === "manage";
  const currentPlan = plans.find((plan) => plan.code === currentPlanCode);

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid={isManage ? "plan-management" : "trial-plan-selection"}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {isManage ? "Plan" : "Trial conversion"}
          </p>
          <h2 className="mt-1 text-lg font-semibold text-slate-950">
            {isManage ? "Manage plan" : "Choose your plan before the trial ends"}
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            {isManage ? "Switch between Solo and Clinic." : `Trial ends ${dateOnly(trialEndsAt)}.`}
          </p>
        </div>
        {isManage && currentPlan ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Current: {currentPlanLabel || currentPlan.label}
          </span>
        ) : null}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {plans.map((plan) => (
          <PlanCard
            key={plan.code}
            plan={plan}
            isManage={isManage}
            isCurrent={isManage && plan.code === currentPlanCode}
            selectingPlan={selectingPlan}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
}

function PlanCard({ plan, isManage, isCurrent, selectingPlan, onSelect }) {
  const saving = selectingPlan === plan.code;
  const disabled = !!selectingPlan || isCurrent;
  const capacityLines = planCapacityLines(plan);

  return (
    <div
      className={`flex min-h-60 flex-col rounded-xl border p-4 ${
        isCurrent || (!isManage && plan.code === "clinic")
          ? "border-teal-400 bg-teal-50/50 shadow-sm"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-base font-semibold text-slate-950">{plan.label}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">{plan.description}</p>
        </div>
        {isCurrent ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-800">
            <CheckCircle2 className="h-3 w-3" />
            Current
          </span>
        ) : plan.badge ? (
          <span className="rounded-full border border-teal-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-800">
            {plan.badge}
          </span>
        ) : null}
      </div>
      {plan.original_price_centavos && plan.original_price_centavos > plan.monthly_price_centavos ? (
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Temporary price</p>
          <div className="mt-1 flex flex-wrap items-baseline gap-2">
            <p className="text-2xl font-bold tabular-nums text-slate-950">{money(plan.monthly_price_centavos)}</p>
            <p className="text-sm font-semibold tabular-nums text-slate-400 line-through">{money(plan.original_price_centavos)}</p>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-2xl font-bold tabular-nums text-slate-950">{money(plan.monthly_price_centavos)}</p>
      )}
      <p className="mt-1 text-xs text-slate-500">per month</p>
      {capacityLines.length ? (
        <div className="mt-3 grid gap-1 rounded-lg border border-slate-100 bg-white/70 px-3 py-2 text-xs text-slate-600">
          {capacityLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : null}
      <div className="mt-4 grid gap-1.5 text-xs">
        {(plan.features || []).map((feature) => (
          <div key={feature.label} className="flex items-center justify-between gap-3 border-t border-slate-100 pt-1.5 first:border-t-0 first:pt-0">
            <span className="text-slate-600">{feature.label}</span>
            <span className={feature.included ? "font-semibold text-teal-700" : "text-slate-300"}>
              {feature.included ? "Included" : "—"}
            </span>
          </div>
        ))}
      </div>
      <button
        type="button"
        data-testid={isCurrent ? `current-plan-${plan.code}` : `select-plan-${plan.code}`}
        onClick={() => onSelect(plan.code)}
        disabled={disabled}
        className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${
          isCurrent
            ? "border border-teal-200 bg-white text-teal-800"
            : plan.code === "clinic"
              ? "bg-teal-700 text-white hover:bg-teal-800 disabled:bg-teal-500"
              : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 disabled:text-slate-400"
        }`}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {saving ? "Saving..." : isCurrent ? "Current plan" : `${isManage ? "Switch to" : "Choose"} ${plan.label}`}
      </button>
    </div>
  );
}

function planCapacityLines(plan) {
  const doctorLimit = plan.active_doctor_limit_per_branch;
  const branchLimit = plan.active_branch_limit;
  const staffLimit = plan.active_staff_limit;
  if (doctorLimit == null && branchLimit == null && staffLimit == null) return [];
  const lines = [];
  if (doctorLimit != null) {
    lines.push(branchLimit && Number(branchLimit) > 1
      ? `${doctorLimit} dentists per branch`
      : `${doctorLimit} doctor${Number(doctorLimit) === 1 ? "" : "s"}`);
  }
  if (branchLimit != null) {
    lines.push(`${branchLimit} branch${Number(branchLimit) === 1 ? "" : "es"}`);
  }
  if (staffLimit != null) {
    lines.push(`${staffLimit} staff`);
  }
  return lines;
}

function PlanChangeReviewModal({ review, saving, onCancel, onConfirm }) {
  const title = review.isInitialTrial
    ? `Start ${review.nextLabel}`
    : `${review.currentLabel} to ${review.nextLabel}`;
  const busy = saving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
      data-testid="plan-change-review"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Review plan change</p>
            <h3 className="mt-1 text-lg font-bold text-slate-950">{title}</h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Nothing changes until you confirm. Dental OS will update the subscription from the database response after the write succeeds.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="ml-auto rounded p-1 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close plan review"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <ReviewStat label="Current plan" value={review.currentLabel} subvalue={money(review.currentMonthlyCentavos)} />
            <ReviewStat label="New plan" value={review.nextLabel} subvalue={money(review.nextMonthlyCentavos)} />
          </div>

          <div className={`rounded-xl border px-4 py-3 text-sm ${review.toneClass}`}>
            <p className="font-semibold">{review.headline}</p>
            <p className="mt-1 text-xs leading-5">{review.body}</p>
          </div>

          <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-xs text-slate-700 sm:grid-cols-3">
            <div>
              <p className="font-semibold text-slate-900">Unused current plan</p>
              <p className="mt-0.5 tabular-nums">{money(review.unusedPreviousCreditCentavos)}</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">{review.nextLabel} remaining charge</p>
              <p className="mt-0.5 tabular-nums">{money(review.newPlanRemainingChargeCentavos)}</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900">{review.netLabel}</p>
              <p className="mt-0.5 tabular-nums">{review.netDisplay}</p>
            </div>
          </div>

          {review.hasOpenInvoice ? (
            <div className="rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-xs leading-5 text-blue-950">
              There is already an open invoice. The database will void the old invoice, create the replacement invoice for the new plan, and keep any pending proof review attached to the replacement.
            </div>
          ) : null}

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-950">
            Final pesos can change by a small amount because the database calculates proration at the exact confirmation time. This screen is the review step; the database result after confirm is the source of truth.
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="confirm-plan-change"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? "Updating..." : review.isTrial ? "Use after trial" : `Confirm switch to ${review.nextLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewStat({ label, value, subvalue }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-bold text-slate-950">{value}</p>
      <p className="mt-0.5 text-xs text-slate-500">{subvalue} / month</p>
    </div>
  );
}

function buildPlanChangeReview({
  subscription = {},
  currentPlan,
  nextPlan,
  accountCreditBalanceCentavos = 0,
  hasOpenInvoice = false,
}) {
  const currentLabel = currentPlan?.label || subscriptionPlanName(subscription);
  const nextLabel = nextPlan?.label || planLabel(nextPlan?.code);
  const currentMonthlyCentavos = Number(subscription.monthly_price_centavos_locked || currentPlan?.monthly_price_centavos || 0);
  const nextMonthlyCentavos = Number(nextPlan?.monthly_price_centavos || 0);
  const remainingRatio = periodRemainingRatio(subscription.current_period_start, subscription.current_period_end);
  const isTrialing = subscription.status === "trialing";
  const isInitialTrial = isTrialing && subscription.plan_code === "trial";
  const unusedPreviousCreditCentavos = isTrialing ? 0 : Math.round(currentMonthlyCentavos * remainingRatio);
  const newPlanRemainingChargeCentavos = isTrialing ? 0 : Math.round(nextMonthlyCentavos * remainingRatio);
  const netAdjustmentCentavos = newPlanRemainingChargeCentavos - unusedPreviousCreditCentavos;
  const accountCredit = Number(accountCreditBalanceCentavos || 0);
  const creditAppliedCentavos = netAdjustmentCentavos > 0 ? Math.min(netAdjustmentCentavos, accountCredit) : 0;
  const dueNowCentavos = Math.max(netAdjustmentCentavos - creditAppliedCentavos, 0);
  const creditGrantedCentavos = Math.max(-netAdjustmentCentavos, 0);

  if (isTrialing) {
    return {
      code: nextPlan?.code,
      currentLabel: isInitialTrial ? "Trial" : currentLabel,
      nextLabel,
      currentMonthlyCentavos: isInitialTrial ? 0 : currentMonthlyCentavos,
      nextMonthlyCentavos,
      unusedPreviousCreditCentavos,
      newPlanRemainingChargeCentavos,
      netLabel: "Trial difference",
      netDisplay: "No credit or charge today",
      headline: `${nextLabel} becomes the active billing plan.`,
      body: "Trialing accounts have not paid for the current period yet, so switching plans during trial does not create account credit, cash refund, or a plan adjustment invoice. The selected monthly rate becomes the price for future invoices.",
      toneClass: "border-teal-200 bg-teal-50 text-teal-950",
      isTrial: isTrialing,
      isInitialTrial,
      hasOpenInvoice,
    };
  }

  if (netAdjustmentCentavos > 0) {
    return {
      code: nextPlan?.code,
      currentLabel,
      nextLabel,
      currentMonthlyCentavos,
      nextMonthlyCentavos,
      unusedPreviousCreditCentavos,
      newPlanRemainingChargeCentavos,
      netLabel: "Upgrade difference",
      netDisplay: dueNowCentavos > 0 ? money(dueNowCentavos) : "Covered by account credit",
      headline: `Upgrade difference: ${dueNowCentavos > 0 ? money(dueNowCentavos) : "covered by account credit"}.`,
      body: `Dental OS will switch the plan immediately. The unused ${currentLabel} time is credited against the remaining ${nextLabel} charge, existing account credit is applied, and any amount left becomes a plan adjustment invoice for review.`,
      toneClass: "border-blue-200 bg-blue-50 text-blue-950",
      isTrial: isTrialing,
      isInitialTrial,
      hasOpenInvoice,
    };
  }

  if (netAdjustmentCentavos < 0) {
    return {
      code: nextPlan?.code,
      currentLabel,
      nextLabel,
      currentMonthlyCentavos,
      nextMonthlyCentavos,
      unusedPreviousCreditCentavos,
      newPlanRemainingChargeCentavos,
      netLabel: "Downgrade difference",
      netDisplay: money(creditGrantedCentavos),
      headline: `Downgrade difference: ${money(creditGrantedCentavos)} account credit.`,
      body: `Dental OS will switch the plan immediately. The unused ${currentLabel} value is not refunded in cash; it becomes account credit and automatically applies to the next Dental OS invoice.`,
      toneClass: "border-emerald-200 bg-emerald-50 text-emerald-950",
      isTrial: isTrialing,
      isInitialTrial,
      hasOpenInvoice,
    };
  }

  return {
    code: nextPlan?.code,
    currentLabel,
    nextLabel,
    currentMonthlyCentavos,
    nextMonthlyCentavos,
    unusedPreviousCreditCentavos,
    newPlanRemainingChargeCentavos,
    netLabel: "Difference",
    netDisplay: "No difference",
    headline: "No plan difference is expected today.",
    body: "Dental OS will switch the plan after confirmation, and future invoices will use the selected monthly rate.",
    toneClass: "border-slate-200 bg-slate-50 text-slate-800",
    isTrial: isTrialing,
    isInitialTrial,
    hasOpenInvoice,
  };
}

function PlanAdjustmentBreakdown({ invoice }) {
  const meta = invoice.adjustment_metadata || {};
  const previousLabel = planLabel(meta.previous_plan_code);
  const nextLabel = planLabel(meta.next_plan_code);
  const unusedCredit = Number(meta.unused_previous_plan_credit_centavos || 0);
  const newCharge = Number(meta.new_plan_remaining_charge_centavos || 0);
  const creditApplied = Number(meta.account_credit_applied_centavos || 0);

  return (
    <div className="mt-4 grid gap-2 rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-3 text-xs text-blue-950 sm:grid-cols-3">
      <div>
        <p className="font-semibold">Unused {previousLabel} credit</p>
        <p className="mt-0.5 tabular-nums">{money(unusedCredit)}</p>
      </div>
      <div>
        <p className="font-semibold">{nextLabel} charge</p>
        <p className="mt-0.5 tabular-nums">{money(newCharge)}</p>
      </div>
      <div>
        <p className="font-semibold">Account credit applied</p>
        <p className="mt-0.5 tabular-nums">{money(creditApplied)}</p>
      </div>
    </div>
  );
}

function BranchAddOnAdjustmentBreakdown({ invoice }) {
  const meta = invoice?.adjustment_metadata?.branch_add_on_delta || {};
  const before = Number(meta.billable_branch_count_before || 0);
  const after = Number(meta.billable_branch_count_after || 0);
  const unit = Number(meta.add_on_unit_centavos || 150000);
  const monthlyDelta = Number(meta.delta_monthly_centavos || 0);
  const prorated = Number(meta.prorated_add_on_centavos || invoice?.amount_centavos || 0);

  return (
    <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50/70 px-3 py-3 text-xs text-amber-950">
      <p className="font-semibold">Prorated branch add-on</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-4">
        <div>
          <p className="font-semibold">Paid branches</p>
          <p className="mt-0.5 tabular-nums">{before} to {after}</p>
        </div>
        <div>
          <p className="font-semibold">Monthly add-on</p>
          <p className="mt-0.5 tabular-nums">{money(unit)}</p>
        </div>
        <div>
          <p className="font-semibold">Monthly delta</p>
          <p className="mt-0.5 tabular-nums">{money(monthlyDelta)}</p>
        </div>
        <div>
          <p className="font-semibold">Due now</p>
          <p className="mt-0.5 tabular-nums">{money(prorated)}</p>
        </div>
      </div>
    </div>
  );
}

function SubscriptionInvoiceBreakdown({ invoice }) {
  const meta = invoice?.adjustment_metadata || {};
  const branchAddOns = meta.branch_add_ons || {};
  const branchAddOnCount = Number(branchAddOns.billable_branch_count || 0);
  const branchAddOnTotal = Number(branchAddOns.add_on_total_centavos || 0);
  const basePrice = Number(branchAddOns.base_price_centavos || meta.base_price_centavos || meta.next_monthly_price_centavos || 0);
  const creditApplied = Number(invoice?.account_credit_applied_centavos || meta.account_credit_applied_centavos || 0);
  if (creditApplied <= 0 && branchAddOnTotal <= 0) return null;
  const gross = Number(meta.gross_amount_centavos || 0)
    || Number(meta.next_monthly_price_centavos || 0)
    || Number(invoice?.amount_centavos || 0) + creditApplied;

  return (
    <div className="mt-4 rounded-xl border border-teal-100 bg-teal-50/60 px-3 py-3 text-xs text-teal-950">
      <p className="font-semibold">Clinic invoice</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-4">
        <div>
          <p className="font-semibold">Locked base</p>
          <p className="mt-0.5 tabular-nums">{money(basePrice || gross - branchAddOnTotal)}</p>
        </div>
        <div>
          <p className="font-semibold">Branch add-ons</p>
          <p className="mt-0.5 tabular-nums">
            {branchAddOnCount > 0 ? `${branchAddOnCount} × ${money(branchAddOns.add_on_unit_centavos || 150000)} = ${money(branchAddOnTotal)}` : money(0)}
          </p>
        </div>
        <div>
          <p className="font-semibold">Account credit applied</p>
          <p className="mt-0.5 tabular-nums">-{money(creditApplied)}</p>
        </div>
        <div>
          <p className="font-semibold">Amount due</p>
          <p className="mt-0.5 tabular-nums">{money(invoice.amount_centavos)}</p>
        </div>
      </div>
    </div>
  );
}

function SubscriptionPayModal({
  invoice,
  methods,
  amount,
  setAmount,
  paidAt,
  setPaidAt,
  subscription,
  reference,
  setReference,
  file,
  setFile,
  ocrRunning,
  ocrSuggestion,
  submitting,
  error,
  onClose,
  onSubmit,
}) {
  const [methodId] = useState(methods[0]?.id || null);
  const [copied, setCopied] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const method = useMemo(
    () => methods.find((m) => m.id === methodId) || methods[0],
    [methods, methodId],
  );
  const coverageCopy = buildRenewalCoverageCopy({ paidAt, invoice, subscription });
  const qrUrl = method?.qr_image_url || null;
  const hasPaymentMethod = methods.length > 0 && !!method;

  async function copyToClipboard(value, key) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((current) => (current === key ? null : current)), 1500);
    } catch (_) {
      // Clipboard unavailable — silent.
    }
  }

  async function downloadQr() {
    if (!qrUrl || !method) return;
    setDownloading(true);
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
      a.href = objectUrl;
      a.download = `${(method.method_name || "qr").replace(/\s+/g, "-").toLowerCase()}-qr.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[billing] qr download failed", e);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
      data-testid="subscription-pay-modal"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pay subscription</p>
            <p className="mt-0.5 font-mono text-[11px] text-slate-500">{invoice.invoice_number}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded p-1 hover:bg-slate-100"
            data-testid="close-pay-modal"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="flex-1 space-y-4 overflow-y-auto p-5"
          data-testid="upload-proof-form"
        >
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Amount due</p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-slate-950">{money(invoice.amount_centavos)}</p>
          </div>

          {!hasPaymentMethod ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-500">
              No payment method is active yet. Contact Dental OS support.
            </div>
          ) : null}

          {hasPaymentMethod && qrUrl ? (
            <div className="mx-auto w-fit rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <img
                src={qrUrl}
                alt={`${method.method_name} QR`}
                className="h-48 w-48 rounded-lg object-contain"
              />
              <button
                type="button"
                onClick={downloadQr}
                disabled={downloading}
                className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                data-testid="download-qr"
              >
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Download QR
              </button>
            </div>
          ) : hasPaymentMethod ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-center text-xs text-amber-900">
              No QR uploaded for this method yet.
            </div>
          ) : null}

          {hasPaymentMethod ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-semibold text-slate-950">{method.method_name}</p>
                {method.bank_name && method.bank_name !== method.method_name ? (
                  <p className="text-[11px] text-slate-500">{method.bank_name}</p>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-slate-500">Account name</p>
              <p className="text-sm font-medium text-slate-800">{method.account_name}</p>
              <p className="mt-2 text-xs text-slate-500">Account number</p>
              <button
                type="button"
                onClick={() => copyToClipboard(method.account_number, `num-${method.id}`)}
                className="group mt-0.5 -ml-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-mono text-sm font-semibold tabular-nums text-slate-900 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-200"
                aria-label={`Copy ${method.method_name} account number`}
              >
                <span>{method.account_number}</span>
                {copied === `num-${method.id}` ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600" />
                )}
              </button>
              {method.instructions ? (
                <p className="mt-3 rounded-md border border-amber-100 bg-amber-50/60 px-2 py-1.5 text-[11px] text-amber-900">
                  {method.instructions}
                </p>
              ) : null}
            </div>
          ) : null}

          {hasPaymentMethod ? (
            <div className="space-y-3">
              <FilePickerField file={file} onChange={setFile} />

              {ocrRunning ? (
                <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Reading the receipt…
                </div>
              ) : ocrSuggestion && ocrSuggestion !== reference ? (
                <button
                  type="button"
                  onClick={() => setReference(ocrSuggestion)}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                >
                  <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                  <span>Auto-detected:</span>
                  <span className="font-mono font-bold">{ocrSuggestion}</span>
                  <span className="font-normal text-amber-700">— tap to use</span>
                </button>
              ) : null}

              <Field label="Payment reference">
                <input
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  className={INPUT_CLASS}
                  placeholder="GCash / bank reference number"
                  data-testid="payment-reference"
                />
              </Field>

              <Field label="Date paid">
                <input
                  type="date"
                  value={paidAt}
                  max={manilaDateKey()}
                  onChange={(event) => setPaidAt(event.target.value)}
                  className={INPUT_CLASS}
                  data-testid="paid-at"
                />
                {coverageCopy ? (
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500" data-testid="renewal-coverage-copy">
                    {coverageCopy}
                  </p>
                ) : null}
              </Field>

              <Field label="Amount paid">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₱</span>
                  <input
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    className={`${INPUT_CLASS} pl-7 tabular-nums`}
                    inputMode="decimal"
                    data-testid="amount-paid"
                  />
                </div>
              </Field>
            </div>
          ) : null}

          {error ? (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {hasPaymentMethod ? (
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="submit-proof"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Submit proof
            </button>
          ) : null}
        </form>
      </div>
    </div>
  );
}

function FilePickerField({ file, onChange }) {
  const inputId = "billing-proof-file";
  return (
    <Field label="Receipt screenshot or PDF">
      <label
        htmlFor={inputId}
        className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2.5 text-sm transition-colors hover:border-teal-400 hover:bg-teal-50/40"
      >
        <span className="flex min-w-0 items-center gap-2 text-slate-700">
          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="truncate">{file ? file.name : "Choose a file..."}</span>
        </span>
        <span className="shrink-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
          Browse
        </span>
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
        className="sr-only"
        data-testid="proof-file"
      />
    </Field>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1.5 text-sm font-semibold text-slate-950">{value || "-"}</p>
    </div>
  );
}

function BillingTimeLeftCard({ items, fallback }) {
  const visibleItems = Array.isArray(items) ? items.filter(Boolean) : [];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid="billing-time-left">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Time left</p>
      {visibleItems.length > 0 ? (
        <div className="mt-2 space-y-1.5">
          {visibleItems.map((item) => (
            <div key={item.label} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-700">{item.label}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{item.detail}</p>
              </div>
              <p className="shrink-0 text-right text-sm font-semibold text-slate-950">{item.value}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-1.5 text-sm font-semibold text-slate-950">{fallback || "-"}</p>
      )}
    </div>
  );
}

function PaymentProofModal({ submission, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
      data-testid="payment-proof-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Payment proof</p>
            <h3 className="mt-1 text-lg font-bold text-slate-950">View payment</h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">{paymentProofModalCopy(submission.status)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded p-1 hover:bg-slate-100"
            aria-label="Close payment proof"
            data-testid="close-payment-proof-modal"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="px-5 pb-5">
          <PaymentProofStatus submission={submission} />
        </div>
      </div>
    </div>
  );
}

function PaymentProofStatus({ submission }) {
  const status = submission?.status || "unknown";
  const isRejected = status === "rejected";
  const isPending = status === "pending_review";
  const isApproved = status === "approved";
  const [receiptUrl, setReceiptUrl] = useState(null);
  const Icon = isRejected ? AlertTriangle : isApproved ? CheckCircle2 : Receipt;
  const tone = isRejected
    ? "border-rose-200 bg-rose-50 text-rose-900"
    : isApproved
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : "border-blue-200 bg-blue-50 text-blue-900";
  const title = isRejected
    ? "Last proof was rejected."
    : isPending
      ? "Payment submitted."
      : isApproved
        ? "Payment proof approved."
        : "Payment proof status.";

  useEffect(() => {
    let cancelled = false;
    setReceiptUrl(null);
    if (!submission?.receipt_storage_path) return undefined;

    (async () => {
      const bucket = supabase.storage.from(PROOF_BUCKET);
      if (typeof bucket.createSignedUrl === "function") {
        const { data, error } = await bucket.createSignedUrl(submission.receipt_storage_path, 60 * 10);
        if (!cancelled && !error) setReceiptUrl(data?.signedUrl || null);
        return;
      }
      const { data } = bucket.getPublicUrl(submission.receipt_storage_path);
      if (!cancelled) setReceiptUrl(data?.publicUrl || null);
    })();

    return () => { cancelled = true; };
  }, [submission?.receipt_storage_path]);

  return (
    <div className={`mt-4 flex items-start gap-2 rounded-xl border px-3 py-3 text-sm ${tone}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <p className="font-semibold">{title}</p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span>Status: {formatStatus(status)}</span>
          <span>Uploaded: {dateTime(submission.submitted_at)}</span>
          {submission.payment_reference ? <span>Reference: {submission.payment_reference}</span> : null}
        </div>
        {receiptUrl ? (
          <a
            href={receiptUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold underline underline-offset-4"
          >
            View uploaded receipt
            <FileText className="h-3.5 w-3.5" />
          </a>
        ) : null}
        {isRejected ? (
          <p className="mt-1 text-xs">{submission.review_notes || "Submit a new payment proof."}</p>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function money(centavos) {
  if (centavos == null) return "-";
  return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(centavos || 0) / 100);
}

function historyMoney(centavos) {
  const value = Number(centavos || 0);
  if (value < 0) return `-${money(Math.abs(value))}`;
  return money(value);
}

function normalizeBillingHistoryPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { can_view: false, can_retry: false, rows: [] };
  }
  return {
    can_view: Boolean(payload.can_view),
    can_retry: Boolean(payload.can_retry),
    rows: Array.isArray(payload.rows) ? payload.rows : [],
  };
}

function normalizeBillingHistoryRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      ...row,
      history_id: row.history_id || `${row.source_table || "invoice"}:${row.source_id || row.invoice_id || row.invoice_number}`,
      status: normalizeHistoryStatus(row.status),
      amount_centavos: Number(row.amount_centavos || 0),
      line_items: Array.isArray(row.line_items) ? row.line_items : [],
      payment_info: row.payment_info && typeof row.payment_info === "object" ? row.payment_info : {},
    }))
    .sort((a, b) => {
      const dateDiff = new Date(b.issued_date || 0).getTime() - new Date(a.issued_date || 0).getTime();
      if (dateDiff !== 0) return dateDiff;
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
}

function normalizeHistoryStatus(status) {
  const normalized = formatStatus(status);
  if (BILLING_HISTORY_STATUS_OPTIONS.includes(normalized)) return normalized;
  return "Pending";
}

function billingHistoryStatusMeta(status) {
  return BILLING_HISTORY_STATUS_META[normalizeHistoryStatus(status)] || BILLING_HISTORY_STATUS_META.Pending;
}

function paymentMethodCopy(paymentInfo = {}) {
  const method = paymentInfo.method_label || paymentInfo.method || "-";
  const lastFour = paymentInfo.last_four ? ` ending in ${paymentInfo.last_four}` : "";
  return `${method}${lastFour}`;
}

function safeFileSegment(value) {
  return String(value || "billing-history")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "billing-history";
}

function safeDownloadFileSegment(value) {
  return String(value || "invoice")
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "invoice";
}

function billingHistoryZipFilename(clinicName) {
  return `${safeFileSegment(clinicName || "clinic")}-billing-history-${manilaDateKey()}.zip`;
}

function manilaDateKey(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function normalizePlanOptions(rows) {
  const byCode = new Map((Array.isArray(rows) ? rows : []).map((plan) => [plan.code, plan]));
  return PLAN_OPTIONS.map((fallback) => {
    const row = byCode.get(fallback.code) || {};
    const basePriceCentavos = row.monthly_price_centavos ?? fallback.monthly_price_centavos;
    const activePriceCentavos = row.locked_price_centavos ?? row.current_price_centavos ?? basePriceCentavos;
    const originalPriceCentavos = row.original_price_centavos
      ?? row.standard_price_centavos
      ?? (basePriceCentavos > activePriceCentavos ? basePriceCentavos : fallback.original_price_centavos);
    return {
      ...fallback,
      label: row.display_name || row.label || fallback.label,
      monthly_price_centavos: activePriceCentavos,
      original_price_centavos: originalPriceCentavos > activePriceCentavos ? originalPriceCentavos : null,
      active_doctor_limit_per_branch: row.active_doctor_limit_per_branch ?? fallback.active_doctor_limit_per_branch,
      active_branch_limit: row.active_branch_limit ?? fallback.active_branch_limit,
      active_staff_limit: row.active_staff_limit ?? fallback.active_staff_limit,
    };
  });
}

function isFoundingSubscription(subscription = {}) {
  return Boolean(
    subscription.founding_cohort
      || subscription.rate_state === "cofounding_active"
      || (subscription.cofounding_rate_granted_at && !subscription.cofounding_rate_forfeited_at)
  );
}

function subscriptionPlanName(subscription = {}) {
  if (subscription.plan_code === "clinic" && isFoundingSubscription(subscription)) {
    return "Clinic - Founding 50";
  }
  if (subscription.plan_code === "solo" && isFoundingSubscription(subscription)) {
    return "Solo - Founding 50";
  }
  return subscription.plan_name || planLabel(subscription.plan_code);
}

function isBillingPortalPayload(value) {
  return !!value
    && typeof value === "object"
    && ("subscription" in value || "tenant" in value || "open_invoice" in value);
}

function isPayableInvoice(invoice) {
  return !!invoice?.id && Number(invoice.amount_centavos || 0) > 0;
}

function buildUpcomingPriceCopy(subscription = {}, planPrice = null, branchAddOns = null) {
  if (!["solo", "clinic"].includes(subscription.plan_code)) return null;
  const nextInvoiceDate = subscription.status === "trialing"
    ? (subscription.trial_ends_at || subscription.current_period_end)
    : (subscription.next_invoice_date || subscription.current_period_end);
  if (!nextInvoiceDate) return null;
  if (branchAddOns?.hasAddOns) {
    return `Next billing on ${dateOnly(nextInvoiceDate)} will use ${money(branchAddOns.basePriceCentavos)} base + ${money(branchAddOns.addOnTotalCentavos)} branch add-ons = ${money(branchAddOns.totalCentavos)}.`;
  }
  if (subscription.status === "trialing" && Number(subscription.monthly_price_centavos_locked || 0) > 0) {
    return `Next billing on ${dateOnly(nextInvoiceDate)} will use ${money(subscription.monthly_price_centavos_locked)}.`;
  }
  if (isFoundingSubscription(subscription) && Number(subscription.monthly_price_centavos_locked || 0) > 0) {
    return `Next billing on ${dateOnly(nextInvoiceDate)} will use ${money(subscription.monthly_price_centavos_locked)}.`;
  }
  if (!planPrice?.current_price_centavos) return null;
  const nextEffectiveDate = planPrice.next_effective_date;
  const amount = nextEffectiveDate && nextEffectiveDate <= nextInvoiceDate
    ? planPrice.next_price_centavos
    : planPrice.current_price_centavos;
  if (!amount) return null;
  return `Next billing on ${dateOnly(nextInvoiceDate)} will use ${money(amount)}.`;
}

function buildLiveBranchAddOnBreakdown(branchAddOns = {}, subscription = {}) {
  const billableBranchCount = Number(branchAddOns?.billable_branch_count || 0);
  const addOnUnitCentavos = Number(branchAddOns?.add_on_unit_centavos || 150000);
  const addOnTotalCentavos = Number(
    branchAddOns?.add_on_total_centavos ?? (billableBranchCount * addOnUnitCentavos)
  );
  const basePriceCentavos = Number(
    branchAddOns?.base_price_centavos
    || subscription.monthly_price_centavos_locked
    || 0
  );

  return {
    activeBranchCount: Number(branchAddOns?.active_branch_count || 0),
    addOnTotalCentavos,
    addOnUnitCentavos,
    basePriceCentavos,
    billableBranchCount,
    hasAddOns: billableBranchCount > 0 || addOnTotalCentavos > 0,
    includedBranchCount: Number(branchAddOns?.included_branch_count || 0),
    totalCentavos: basePriceCentavos + addOnTotalCentavos,
  };
}

function buildBillingTimeLeftItems(subscription = {}) {
  const trialEndsAt = subscription.trial_ends_at || (subscription.status === "trialing" ? subscription.current_period_end : null);
  if (subscription.status === "trialing" && trialEndsAt) {
    return [{
      label: "Free trial",
      value: timeLeftText(trialEndsAt),
      detail: `Remaining in trial · Ends ${dateOnly(trialEndsAt)}`,
    }];
  }
  if (subscription.current_period_end) {
    return [{
      label: "Billing period",
      value: timeLeftText(subscription.current_period_end),
      detail: `For billing period · Ends ${dateOnly(subscription.current_period_end)}`,
    }];
  }
  return [];
}

function invoiceTitle(invoice) {
  if (invoice?.invoice_kind === "plan_change_adjustment") return "Plan adjustment invoice";
  if (invoice?.invoice_kind === "branch_addon_adjustment") return "Branch add-on adjustment invoice";
  return "Open invoice";
}

function isAdjustmentInvoice(invoice) {
  return ["plan_change_adjustment", "branch_addon_adjustment"].includes(invoice?.invoice_kind);
}

function planLabel(planCode) {
  const match = PLAN_OPTIONS.find((plan) => plan.code === planCode);
  return match?.label || formatStatus(planCode);
}

function pesosToCentavos(value) {
  const parsed = Number(String(value || "").replace(/,/g, ""));
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
}

function periodRemainingRatio(start, end) {
  const startMs = new Date(start || Date.now()).getTime();
  const endMs = new Date(end || Date.now()).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 1;
  const remainingMs = Math.min(Math.max(endMs - Date.now(), 0), endMs - startMs);
  return remainingMs / (endMs - startMs);
}

function timeLeftText(end) {
  const endMs = new Date(end || "").getTime();
  if (!Number.isFinite(endMs)) return "-";
  const remainingMs = endMs - Date.now();
  if (remainingMs <= 0) return "Ended";
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  if (remainingMs < hourMs) return "Less than 1 hour left";
  if (remainingMs < dayMs) {
    const hours = Math.ceil(remainingMs / hourMs);
    return `${hours} ${hours === 1 ? "hour" : "hours"} left`;
  }
  const days = Math.ceil(remainingMs / dayMs);
  return `${days} ${days === 1 ? "day" : "days"} left`;
}

function dateOnly(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function dateKeyOnly(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function addOneMonthDateKey(dateKey) {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const lastDay = new Date(Date.UTC(nextYear, nextMonth, 0)).getUTCDate();
  const nextDay = Math.min(day, lastDay);
  return [
    nextYear,
    String(nextMonth).padStart(2, "0"),
    String(nextDay).padStart(2, "0"),
  ].join("-");
}

function dateKeyLabel(dateKey) {
  return dateKey ? dateOnly(`${dateKey}T00:00:00+08:00`) : "-";
}

function buildRenewalCoverageCopy({ paidAt, invoice, subscription }) {
  const paidDateKey = dateKeyOnly(paidAt);
  if (!paidDateKey || !invoice?.id) return null;
  const trialEndKey = dateKeyOnly(subscription?.trial_ends_at || invoice?.period_start);
  const coverageStartKey = trialEndKey && trialEndKey > paidDateKey ? trialEndKey : paidDateKey;
  const coverageEndKey = addOneMonthDateKey(coverageStartKey);
  if (!coverageStartKey || !coverageEndKey) return null;

  const coverage = `${dateKeyLabel(coverageStartKey)} to ${dateKeyLabel(coverageEndKey)}`;
  if (trialEndKey && trialEndKey > paidDateKey) {
    return `Your trial remains active until ${dateKeyLabel(trialEndKey)}. If approved, this renewal covers ${coverage}.`;
  }
  return `If approved, this renewal covers ${coverage}.`;
}

function billingPeriodText(subscription = {}) {
  if (!subscription.current_period_start || !subscription.current_period_end) return "-";
  return `${dateOnly(subscription.current_period_start)} ~ ${dateOnly(subscription.current_period_end)}`;
}

function dateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function readOnlyBillingCopy(reason) {
  if (reason === "tenant_suspended") {
    return {
      title: "Account is suspended.",
      body: "You can view records and schedules, but new work is blocked until Dental OS reactivates the tenant.",
    };
  }
  if (reason === "pending_payment" || reason === "self_serve_trial_expired") {
    return {
      title: "Payment is pending.",
      body: "You can view records and schedules, but new work is blocked until the first Dental OS payment is submitted.",
    };
  }

  return {
    title: "Read-only mode is active.",
    body: "You can view records and schedules, but new work is blocked until billing is restored.",
  };
}

function isWithinTrialPayNowWindow(trialEndsAt, now = new Date()) {
  if (!trialEndsAt) return false;
  const trialEnd = new Date(trialEndsAt);
  if (Number.isNaN(trialEnd.getTime())) return false;
  const unlockAt = new Date(trialEnd.getTime() - 3 * 24 * 60 * 60 * 1000);
  return now >= unlockAt;
}

function formatStatus(value) {
  if (!value) return "-";
  return String(value).split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function paymentProofModalCopy(status) {
  if (status === "approved") return "Payment proof has been approved by Dental OS.";
  if (status === "rejected") return "Payment proof was rejected. Review the notes before submitting again.";
  return "Awaiting Dental OS review.";
}
